import { parseAsString, useQueryState } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search, Settings2 } from 'lucide-react'
import { useStaff } from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/search-input'
import { ResultCount } from '@/components/list-filters'
import { resultCount } from '@/lib/utils'

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'Project Manager', label: 'Project Manager' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Finance', label: 'Finance' },
]

const ROLE_COLORS: Record<string, 'default' | 'secondary' | 'outline'> = {
  'Project Manager': 'default',
  Admin: 'secondary',
  Sales: 'outline',
  Operations: 'outline',
  Finance: 'secondary',
}

export function StaffPage() {
  const [search, setSearch] = useUrlSearchText()
  const [role, setRole] = useQueryState('role', parseAsString.withDefault('all'))

  const { data, isLoading } = useStaff({
    search: search || undefined,
    role: role === 'all' ? undefined : role,
  })

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Staff</h1>
        <Button asChild variant="outline">
          <Link to="/staff/defaults">
            <Settings2 />
            Project role defaults
          </Link>
        </Button>
      </div>

      <div className="flex flex-col gap-3 md:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <SearchInput
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={role} onValueChange={(v) => void setRole(v)}>
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ResultCount className="md:self-center">{resultCount(data?.length, 'person', 'people')}</ResultCount>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : data?.length ? (
              data.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Link
                      to="/staff/$staffId"
                      params={{ staffId: user.uuid }}
                      className="font-medium text-primary hover:underline"
                    >
                      {user.name}
                    </Link>
                    {user.title && <p className="text-xs text-muted-foreground">{user.title}</p>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell>
                    {user.role ? (
                      <Badge variant={ROLE_COLORS[user.role] ?? 'outline'}>
                        {user.role}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  No staff members found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden">
        {isLoading ? (
          <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : data?.length ? (
          data.map((user) => (
            <Link
              key={user.id}
              to="/staff/$staffId"
              params={{ staffId: user.uuid }}
              className="block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-medium text-primary">{user.name}</p>
                  {user.title && <p className="mt-0.5 text-xs text-muted-foreground">{user.title}</p>}
                  <p className="mt-0.5 break-all text-xs text-muted-foreground">{user.email}</p>
                </div>
                {user.role && (
                  <Badge variant={ROLE_COLORS[user.role] ?? 'outline'} className="shrink-0">
                    {user.role}
                  </Badge>
                )}
              </div>
            </Link>
          ))
        ) : (
          <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
            No staff members found
          </div>
        )}
      </div>
    </div>
  )
}
